import { Table } from "antd";
import { nanoid } from "nanoid";
import Button from "../../Button";
import Select from "react-select";
import update from "immutability-helper";
import Dialog from "@mui/material/Dialog";
import constants from "../../../constants";
import Popup from "../../CommonComponents/Popup";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import { getDataFromStorage } from "../../../util";
import DialogTitle from "@mui/material/DialogTitle";
import { HTML5Backend } from "react-dnd-html5-backend";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import WidgetConfiguration from "./WidgetConfiguration";
import AlertContext from "../../../context/AlertContext";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import useAxiosPrivate from "../../../Hooks/useAxiosPrivate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useCommonFunctionHooks from "../../../Hooks/useCommonFunctionHooks";
import React, { useEffect, useState, useContext, useCallback } from "react";

import {
  faEdit,
  faTrashCan,
  faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";

const WidgetOptions = [
  { label: "SCORES", value: 5 },
  { label: "ATTENDANCE", value: 1 },
  { label: "LEADERBOARD", value: 2 },
  { label: "PERCENTILE", value: 3 },
  { label: "ASSIGNMENT STATUS", value: 4 },
];

const type = "DraggableRow"; // unique identifier for dnd

const DraggableRow = ({ index, moveRow, className, style, ...restProps }) => {
  const ref = React.useRef();

  const [{ isDragging }, drag] = useDrag({
    type,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
      opacity: monitor.isDragging() ? 0.5 : 1,
    }),
  });

  const [, drop] = useDrop({
    accept: type,
    hover: (item, monitor) => {
      if (item.index !== index) {
        moveRow(item.index, index);
        item.index = index;
      }
    },
  });

  drag(drop(ref));

  return (
    <tr
      ref={ref}
      {...restProps}
      className={className}
      style={{ ...style, opacity: isDragging ? 0.5 : 1 }}
    />
  );
};

const components = {
  body: {
    row: DraggableRow,
  },
};

const DashboardForm = () => {
  const Alert = useContext(AlertContext);

  const axios = useAxiosPrivate();
  const useCommonFunctionHks = useCommonFunctionHooks();

  const [dashboardName, setDashboardName] = useState(
    Alert.isDashBoardEdit
      ? Alert.currentEditDashboardMetaData?.boardDetails[0]?.name
      : ""
  );

  const [dashboardDescription, setDashboardDescription] = useState(
    Alert.isDashBoardEdit
      ? Alert.currentEditDashboardMetaData?.boardDetails[0]?.description
      : ""
  );

  const [selectedWidgets, setSelectedWidgets] = useState(
    Alert.isDashBoardEdit
      ? Alert.currentEditDashboardMetaData?.widgets?.map((i) => {
          return {
            wid: i.id,
            widgetname: i.name,
            wtype: i.widget_id,
            orderno: i.orderno,
          };
        })
      : []
  );

  const [editedWidget, setEditedWidget] = useState();
  const [currentWidget, setCurrentWidget] = useState(0);

  const [viewers, setViewers] = useState(
    Alert.isDashBoardEdit
      ? Alert.currentEditDashboardMetaData.boardUser.data.map((i) => {
          return { label: i.userName, value: i.userId };
        })
      : []
  );

  const [isEdit, setIsEdit] = useState(false);
  const [adminList, setAdminList] = useState([]);
  const [saveTriggered, setSaveTriggered] = useState(false);
  const [showConfigPopUp, setShowConfigPopUp] = useState(false);
  const [updateTriggered, setUpdateTriggered] = useState(false);
  const [widgetSaveDisable, setWidgetSaveDisable] = useState(true);
  const [dataOfCurrentWidget, setDataofCurrentWidget] = useState({});

  const openSelectedDashBoard = async (id) => {
    try {
      Alert.setIsLoaded(true);

      let response = await axios.get(
        "node/admin/dashboard/" + id,
        useCommonFunctionHks.getHeaderDetailsForAxios()
      );

      Alert.setIsLoaded(false);
      Alert.setCurrentDashboardMetaData(response.data.data);
      Alert.navigate("/dashboard");
    } catch (err) {
      Alert.setIsLoaded(false);
      useCommonFunctionHks.getCatchBlockDetails(err);
    }
  };

  const handleCloseResponsePopup = (id) => {
    Alert.setIsDashBoardEdit(false);
    openSelectedDashBoard(id);
    useCommonFunctionHks.handleClose();
  };

  const handleWarningResponsePopupClose = () => {
    useCommonFunctionHks.handleClose();
  };

  const moveRow = useCallback(
    (dragIndex, hoverIndex) => {
      const dragRow = selectedWidgets[dragIndex];

      const updatedWidgets = update(selectedWidgets, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, dragRow],
        ],
      }).map((widget, index) => ({
        ...widget,
        orderno: index + 1, // Update orderno based on new position
      }));

      setSelectedWidgets(updatedWidgets);
    },
    [selectedWidgets]
  );

  const handleResponse = (title, msg, dId) => {
    Alert.setShowNotify({
      show: true,
      title: title,
      primaryButtonFunction:
        title == "Success"
          ? () => {
              handleCloseResponsePopup(dId);
            }
          : handleWarningResponsePopupClose,
      msg: msg,
    });
  };

  const hanldeViewersChange = (e) => {
    setViewers(e);
  };

  const handleWidgetChange = (selectedOptions) => {
    setCurrentWidget(selectedOptions);
  };

  const handleShowPopUp = () => {
    setShowConfigPopUp(true);
  };

  const handleClose = () => {
    setShowConfigPopUp(false);
    setIsEdit(false);
  };

  const handleUpdateDashboard = async (status) => {
    try {
      let widgets = selectedWidgets.map((i, ind) => {
        return { id: i.wid, name: i.widgetname, orderno: ind + 1 };
      });

      let name = dashboardName;
      let description = dashboardDescription;
      let userIds = viewers.map((i) => i.value);

      const upadtePayload = {
        name,
        userIds,
        widgets,
        description,
      };

      if (Alert.dashBoardId != null) {
        const response = await axios.put(
          "node/admin/dashboard/" + Alert.dashBoardId,
          upadtePayload,
          useCommonFunctionHks.getHeaderDetailsForAxios()
        );

        if (status !== constants.BOARD_STATUS.PUBLISHED) {
          Alert.setDbStatus(constants.BOARD_STATUS.DRAFT);
        }

        if (response.data.resultCode == constants.RESULT_STATUS.SUCCESS) {
          handleResponse("Success", response.data.msg, Alert.dashBoardId);
        } else {
          handleResponse("Warning", response.data.msg);
        }
      }
    } catch (err) {
      useCommonFunctionHks.getCatchBlockDetails(err);
    }
  };

  const handlePublishDashboard = async () => {
    try {
      const response = await axios.patch(
        "node/admin/dashboard/" + Alert.dashBoardId + "/status",
        { status: constants.BOARD_STATUS.PUBLISHED },
        useCommonFunctionHks.getHeaderDetailsForAxios()
      );

      Alert.setDbStatus(2);

      if (response.data.resultCode == constants.RESULT_STATUS.SUCCESS) {
        handleResponse("Success", response.data.msg);
      } else {
        handleResponse("Warning", response.data.msg);
      }
    } catch (err) {
      useCommonFunctionHks.getCatchBlockDetails(err);
    }
  };

  const handleSaveDashboard = async () => {
    try {
      let dashBoardData = {};
      dashBoardData.name = dashboardName;
      dashBoardData.description = dashboardDescription;
      dashBoardData.userId = getDataFromStorage("learnerid");
      dashBoardData.viewers = viewers.map((user) => user.value);

      if (Alert.dashBoardId == 0 && !dashboardName == "") {
        const response = await axios.post(
          "node/admin/dashboard",
          dashBoardData,
          useCommonFunctionHks.getHeaderDetailsForAxios()
        );

        Alert.setDashboardId(response?.data?.data?.id);
        Alert.setDbStatus(0);
      }
    } catch (err) {
      useCommonFunctionHks.getCatchBlockDetails(err);
    }
  };

  const handleDeleteWidget = async (wid) => {
    try {
      let response = await axios.delete(
        "node/admin/dashboard/widget/" + wid,
        useCommonFunctionHks.getHeaderDetailsForAxios()
      );

      let widgets = selectedWidgets.filter((i) => i.wid != wid);

      setSelectedWidgets(widgets);

      if (response.data.resultCode == constants.RESULT_STATUS.SUCCESS) {
        handleResponse("DeletedSuccess", response.data.msg);
      } else {
        handleResponse("Warning", response.data.msg);
      }
    } catch (err) {
      useCommonFunctionHks.getCatchBlockDetails(err);
    }
  };

  const getIndexInParent = (el) =>
    Array.from(el.parentNode.children).indexOf(el);

  const handleEditSpecificWidget = async (id, type) => {
    try {
      let response = await axios.get(
        "node/admin/dashboard/widget/" + id,
        useCommonFunctionHks.getHeaderDetailsForAxios()
      );

      setIsEdit(true);
      setDataofCurrentWidget(response.data.data);

      let wid = WidgetOptions.find((i) => i.value == type);

      setEditedWidget(wid);
      setShowConfigPopUp(true);
    } catch (err) {
      useCommonFunctionHks.getCatchBlockDetails(err);
    }
  };

  const defaultColumns = [
    {
      width: "300px",
      editable: false,
      title: "Widget Name",
      dataIndex: "widgetname",
    },
    {
      width: "100px",
      title: "Actions",
      dataIndex: "Actions",
      render: (_, record) =>
        selectedWidgets.length >= 1 ? (
          <>
            <FontAwesomeIcon
              icon={faTrashCan}
              className="faTrashCan"
              onClick={() => handleDeleteWidget(record.wid)}
              style={{ cursor: "pointer", marginRight: "16px" }}
            />
            <FontAwesomeIcon
              icon={faEdit}
              className="faEdithCan"
              style={{ cursor: "pointer" }}
              onClick={() => handleEditSpecificWidget(record.wid, record.wtype)}
            />
          </>
        ) : null,
    },
  ];

  useEffect(() => {
    console.log("selectedWidgets>>>>>", selectedWidgets);
  }, [selectedWidgets]);

  const handleReorder = (dragIndex, draggedIndex) => {
    setSelectedWidgets((oldState) => {
      const newState = [...oldState];
      const item = newState.splice(dragIndex, 1)[0];
      newState.splice(draggedIndex, 0, item);
      return newState;
    });
  };

  const Columns = defaultColumns.map((col) => {
    if (!col.editable) {
      return col;
    }

    return {
      ...col,
      onCell: (record) => ({
        record,
        title: col.title,
        editable: col.editable,
        dataIndex: col.dataIndex,
      }),
    };
  });

  const customStylesForPendingTests = {
    control: (base, state) => ({
      ...base,
      width: "246px",
      color: "000000",
      textAlign: "left",
      borderRadius: "4px",
      paddingLeft: "10px",
      marginBottom: "-5px",
      height: "20px !important",
      backgroundColor: "#ffffff",
      border: "1px solid #BDC1C6",
      innerHeight: "20px !important",
    }),
    option: (styles, { data, isDisabled, isFocused, isSelected }) => {
      return {
        ...styles,
        padding: "8px",
        fontSize: "12px",
        fontWeight: "600",
        textAlign: "left",
        lineHeight: "15px",
        fontFamily: "Inter",
        color: isSelected ? "#000000" : undefined,
        backgroundColor: isSelected
          ? "#ffffff"
          : isFocused
          ? "#e9ecef"
          : undefined,
      };
    },
    singleValue: (styles) => ({
      ...styles,
      color: "#000000",
      fontSize: "12px",
      fontWeight: "600",
      lineHeight: "15px",
      fontFamily: "Inter",
      fontStyle: "normal",
      textAlign: "center",
      width: "max-content",
    }),
    placeholder: (base) => ({
      ...base,
      fontSize: "14px",
      color: "#BDC1C6",
      fontWeight: "400",
      lineHeight: "20px",
      fontFamily: "Inter",
      fontStyle: "normal",
    }),
    menuList: (base) => ({
      ...base,
      height: "150px",
    }),
  };

  useEffect(() => {
    try {
      Alert.setDbStatus(
        Alert.isDashBoardEdit
          ? Alert.currentEditDashboardMetaData?.boardDetails[0]?.status
          : null
      );
      Alert.setDashboardId(
        Alert.isDashBoardEdit
          ? Alert.currentEditDashboardMetaData?.boardDetails[0]?.id
          : 0
      );

      const adminsResponse = async () => {
        let response = await axios.get("node/admin/users", {
          params: {
            role: "admin",
          },
          headers: {
            "Content-type": "application/json",
          },
        });

        let transformedData = response?.data?.data.map((item) => {
          return {
            value: item.id,
            label: `${item.firstname} ${item.lastname}`,
          };
        });

        setAdminList(transformedData);
      };

      adminsResponse();
    } catch (err) {
      useCommonFunctionHks.getCatchBlockDetails(err);
    }
  }, []);

  const handleSaveWidget = () => {
    setSaveTriggered(true);
  };

  const handleUpdateWidget = () => {
    setUpdateTriggered(true);
  };

  const handleBacktoDashboards = () => {
    Alert.setIsDashBoardEdit(false);
    Alert.navigate("/dashboards");
  };

  return (
    <div className="dashBoardForm">
      <div
        className="dashbaordFormback"
        style={{
          right: "20px",
          textAlign: "left",
          position: "relative",
          marginBottom: "10px",
        }}
      >
        <FontAwesomeIcon
          icon={faArrowLeft}
          className="adminTestBackBtn"
          onClick={handleBacktoDashboards}
        />
        &nbsp;
        <span className="adminTestBack" onClick={handleBacktoDashboards}>
          {" "}
          Back{" "}
        </span>
      </div>
      {Alert.isCommonPopup && (
        <div className="popup-overlay">
          <Popup />
        </div>
      )}
      <div className="dashBoardNameContainer">
        <label htmlFor="dashboardName" className="dashBoardName">
          Dashboard Name <span style={{ color: "red", width: "4px" }}>*</span> :
        </label>
        <input
          required
          type="text"
          id="dashboardName"
          value={dashboardName}
          onBlur={handleSaveDashboard}
          onChange={(e) => setDashboardName(e.target.value)}
        />
      </div>
      <div className="dashBoardDescriptionContainer">
        <label htmlFor="dashboardDescription" className="dashboarddescription">
          Dashboard Description:
        </label>
        <textarea
          required
          id="dashboardDescription"
          value={dashboardDescription}
          onChange={(e) => setDashboardDescription(e.target.value)}
        />
      </div>
      <div className="selectWidgetContainer">
        <label htmlFor="widgetSelect" className="widgetSelect">
          Shared With:
        </label>
        <div className="selectViewers">
          <Select
            isMulti
            isClearable
            value={viewers}
            options={adminList}
            placeholder={"Choose Viewers"}
            onChange={hanldeViewersChange}
          />
        </div>
        {viewers.length > 0 && (
          <div className="viewersList">
            <ul>
              {viewers.map((i) => (
                <li> {i.label} </li>
              ))}
            </ul>
          </div>
        )}
        <label htmlFor="widgetSelect" className="widgetSelect">
          Select Widgets:
        </label>
        <div className="addAndSelect">
          <Select
            required
            id="widgetSelect"
            value={currentWidget}
            options={WidgetOptions}
            onChange={handleWidgetChange}
            styles={customStylesForPendingTests}
          />
          <Button
            size={"sm"}
            onClick={handleShowPopUp}
            disable={currentWidget == 0}
            hierarchy={{
              type: "primary",
              buttonText: "Add",
            }}
          />
        </div>

        <div className="tableForWidgets">
          <DndProvider backend={HTML5Backend}>
            <Table
              key={nanoid()}
              bordered={true}
              columns={Columns}
              pagination={false}
              components={components}
              dataSource={selectedWidgets}
              onRow={(record, index) => ({
                index,
                moveRow,
              })}
            />
          </DndProvider>
        </div>
      </div>
      {showConfigPopUp && (
        <div className="widgetForm1">
          <Dialog
            scroll={"paper"}
            onClose={handleClose}
            open={showConfigPopUp}
            className="widgetForm"
            aria-labelledby="scroll-dialog-title"
            aria-describedby="scroll-dialog-description"
          >
            <DialogTitle
              sx={{ m: 0, p: 2 }}
              id="customized-dialog-title"
              style={{ color: "#f55533" }}
            >{`WIDGET CONFIGURATION - ${
              isEdit ? editedWidget.label : currentWidget.label
            } `}</DialogTitle>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                top: 8,
                right: 8,
                position: "absolute",
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>

            <DialogContent dividers={true}>
              <WidgetConfiguration
                isEdit={isEdit}
                setIsEdit={setIsEdit}
                saveTriggered={saveTriggered}
                id="scroll-dialog-description"
                dashBoardId={Alert.dashBoardId}
                selectedWidgets={selectedWidgets}
                updateTriggered={updateTriggered}
                setSaveTriggered={setSaveTriggered}
                widgetSaveDisable={widgetSaveDisable}
                setShowConfigPopUp={setShowConfigPopUp}
                setSelectedWidgets={setSelectedWidgets}
                setUpdateTriggered={setUpdateTriggered}
                dataOfCurrentWidget={dataOfCurrentWidget}
                setWidgetSaveDisable={setWidgetSaveDisable}
                setDataofCurrentWidget={setDataofCurrentWidget}
                type={isEdit ? editedWidget.value : currentWidget.value}
              />
            </DialogContent>
            <DialogActions>
              <Button
                size={"sm"}
                disable={widgetSaveDisable}
                onClick={isEdit ? handleUpdateWidget : handleSaveWidget}
                hierarchy={{
                  type: "primary",
                  buttonText: isEdit ? "Update" : "Save",
                }}
              />
              <Button
                size={"sm"}
                disable={false}
                onClick={handleClose}
                hierarchy={{
                  buttonText: "Cancel",
                  type: "secondaryGrey",
                }}
              />
            </DialogActions>
          </Dialog>
        </div>
      )}
      <div className="dashBoardSave">
        <button
          className="btnMedium"
          disabled={Alert.dashBoardId == 0}
          onClick={() => {
            handleUpdateDashboard(Alert.dbStatus);
          }}
        >
          Save
        </button>
      </div>
    </div>
  );
};

export default DashboardForm;
